import { normalizePhone } from "@car/auth/roles";
import { z } from "zod";

import { toJalali } from "./service-config";

// Sales/CRM vocabulary shared by the API, the seed and the admin client.
// Pure module: keys, zod shapes and tiny pure helpers only. Persian labels live
// in the admin app (apps/admin/src/lib/crm.ts).

export const pipelineStageKeys = [
  "new",
  "first_call",
  "consult",
  "visit",
  "test_drive",
  "negotiation",
  "pre_contract",
  "contract",
  "won",
  "lost",
] as const;
export type PipelineStageKey = (typeof pipelineStageKeys)[number];

export const closedStageKeys = ["won", "lost"] as const satisfies readonly PipelineStageKey[];

export const isClosedStage = (stage: PipelineStageKey) => stage === "won" || stage === "lost";

export const leadSourceKeys = [
  "website",
  "phone",
  "walk_in",
  "exhibition",
  "referral",
  "divar",
  "bama",
  "instagram",
  "sms_campaign",
  "advertising",
] as const;
export type LeadSourceKey = (typeof leadSourceKeys)[number];

// ---------------------------------------------------------------------------
// Showroom sales tools: test drives and deposits
// ---------------------------------------------------------------------------

export const testDriveStatusKeys = ["scheduled", "done", "no_show", "cancelled"] as const;
export type TestDriveStatusKey = (typeof testDriveStatusKeys)[number];

/** One test drive with its paperwork takes the demo car this long */
export const TEST_DRIVE_MINUTES = 60;

/**
 * Start times of test drives on a sales day, in minutes after midnight (Tehran): every
 * hour from opening while a whole drive still ends before the sales counter closes.
 */
export const testDriveSlots = (open: number, close: number) => {
  const slots: number[] = [];
  for (let at = open; at + TEST_DRIVE_MINUTES <= close; at += TEST_DRIVE_MINUTES) slots.push(at);
  return slots;
};

/** How far ahead the website offers test drive days */
export const TEST_DRIVE_DAYS_AHEAD = 10;

/** How a deposit staff take at the desk was paid: what the «ثبت بیعانه» form offers */
export const manualDepositMethodKeys = [
  "cash",
  "pos",
  "card_to_card",
  "bank_transfer",
  "cheque",
] as const;

/**
 * Every way a deposit is paid. `online` is written only by a gateway's confirmation of a
 * pay-link (deposit-links.ts); staff never choose it.
 */
export const depositMethodKeys = [...manualDepositMethodKeys, "online"] as const;
export type DepositMethodKey = (typeof depositMethodKeys)[number];

export const depositMethodLabels = {
  cash: "نقدی",
  pos: "کارت‌خوان",
  card_to_card: "کارت به کارت",
  bank_transfer: "واریز بانکی",
  cheque: "چک",
  online: "پرداخت آنلاین",
} satisfies Record<DepositMethodKey, string>;

/**
 * A deposit pay-link is open → paid | cancelled in the database; an open one past its time is
 * shown as expired.
 */
export const depositLinkStatusKeys = ["open", "paid", "cancelled", "expired"] as const;
export type DepositLinkStatusKey = (typeof depositLinkStatusKeys)[number];

export const depositLinkStatusLabels = {
  open: "در انتظار پرداخت",
  paid: "پرداخت شد",
  cancelled: "لغو شد",
  expired: "مهلت تمام شد",
} satisfies Record<DepositLinkStatusKey, string>;

// ---------------------------------------------------------------------------
// Bulk import of Leads (CSV from an exhibition sheet, Divar, Instagram…)
// ---------------------------------------------------------------------------

export const IMPORT_MAX_ROWS = 500;

export type ImportRow = { name: string; phone: string; interestVehicle: string; note: string };

/** A Lead's (normalized) phone: mobile 09xxxxxxxxx or a landline with its area code, 11 digits either way */
export const isLeadPhone = (phone: string) => /^0\d{10}$/.test(phone);

/**
 * Why one import row cannot become a Lead, or null when it can. The panel's preview and
 * the server run the same rule, so what the preview accepts is what gets imported.
 */
export function importRowProblem(row: ImportRow): string | null {
  const name = row.name.trim();
  if (name.length < 2) return "نام خالی یا کوتاه است";
  if (name.length > 80) return "نام بیش از ۸۰ نویسه است";
  if (!isLeadPhone(normalizePhone(row.phone))) return "شماره تماس معتبر نیست";
  if (row.interestVehicle.trim().length > 60) return "نام خودرو بیش از ۶۰ نویسه است";
  if (row.note.trim().length > 500) return "یادداشت بیش از ۵۰۰ نویسه است";
  return null;
}

/** Activities a staff member logs by hand */
export const activityKinds = ["call", "meeting", "sms", "whatsapp", "note"] as const;
export type ActivityKind = (typeof activityKinds)[number];

/**
 * Timeline rows the system writes itself; never edited or deleted. `web_request`: the website
 * relayed a repeat request of the buyer, with no staff author (staffId null).
 */
export const systemActivityKinds = ["stage", "assign", "web_request"] as const;
export type TimelineKind = ActivityKind | (typeof systemActivityKinds)[number];

/** Activities that count as "we were in touch with the buyer" */
export const contactKinds = [
  "call",
  "meeting",
  "sms",
  "whatsapp",
] as const satisfies readonly ActivityKind[];

export const callOutcomeKeys = [
  "answered",
  "no_answer",
  "busy",
  "callback",
  "wrong_number",
] as const;
export type CallOutcomeKey = (typeof callOutcomeKeys)[number];

export const callDirectionKeys = ["outbound", "inbound"] as const;
export type CallDirectionKey = (typeof callDirectionKeys)[number];

export const lostReasonKeys = [
  "price",
  "delivery_time",
  "competitor",
  "financing",
  "no_response",
  "changed_mind",
] as const;
export type LostReasonKey = (typeof lostReasonKeys)[number];

export const taskKinds = ["task", "reminder", "followup"] as const;
export type TaskKind = (typeof taskKinds)[number];

export const taskPriorityKeys = ["normal", "high"] as const;
export type TaskPriorityKey = (typeof taskPriorityKeys)[number];

/**
 * Roles that work their own queue (Leads, tasks, call rows) and never the whole team's:
 * they cannot see colleagues' work, adjust wallets, delete orders or customer files.
 */
export const frontlineRoles = ["sales_agent", "support_agent"] as const;
export const isFrontline = (role: string) => frontlineRoles.some((key) => key === role);

/** Roles that see and manage every staff member's tasks; everyone else sees what they gave or got */
export const taskManagerRoles = [
  "superadmin",
  "ceo",
  "sales_manager",
  "crm_manager",
  "service_manager",
] as const;
export const managesAllTasks = (role: string) => taskManagerRoles.some((key) => key === role);

// ---------------------------------------------------------------------------
// Contacting a buyer or customer without a telephony API: tel: links, WhatsApp
// links with a prefilled text, and SMS through the SMS module. Every one is logged.
// ---------------------------------------------------------------------------

/** channel of a contact made from a Lead or a customer file (lead_activity / customer_interaction) */
export const contactChannelKeys = ["call", "sms", "whatsapp"] as const;
export type ContactChannelKey = (typeof contactChannelKeys)[number];

/** The placeholder a message template may hold; replaced with the recipient's name */
export const TEMPLATE_NAME_TOKEN = "{نام}";

export const fillTemplate = (body: string, name: string) =>
  body.replaceAll(TEMPLATE_NAME_TOKEN, name.trim());

/** https://wa.me/ wants the number in international form without «+»: 0912… → 98912… */
export const whatsappLink = (phone: string, text: string) => {
  const digits = normalizePhone(phone);
  const international = digits.startsWith("0") ? `98${digits.slice(1)}` : digits;
  return `https://wa.me/${international}?text=${encodeURIComponent(text)}`;
};

// ---------------------------------------------------------------------------
// Outbound call lists: a queue of customers to phone, built from the data
// ---------------------------------------------------------------------------

export const callListKindKeys = [
  "service_due",
  "document_expiring",
  "birthday",
  "inactive",
] as const;
export type CallListKindKey = (typeof callListKindKeys)[number];

/** Outcomes after which the customer still needs another try */
export const retryCallOutcomes = [
  "no_answer",
  "busy",
  "callback",
] as const satisfies readonly CallOutcomeKey[];

/**
 * Days from `now` (Tehran calendar) to the customer's next Jalali birthday when it falls
 * within `days` days (0 = today), otherwise null
 */
export function birthdayInDays(birthDate: Date, now: Date, days: number) {
  const birth = toJalali(birthDate);
  for (let offset = 0; offset < days; offset += 1) {
    const day = toJalali(new Date(now.getTime() + offset * DAY_MS));
    if (day.month === birth.month && day.day === birth.day) return offset;
  }
  return null;
}

export const tierKeys = ["bronze", "silver", "gold", "vip"] as const;
export type TierKey = (typeof tierKeys)[number];

export const campaignStatusKeys = ["queued", "sending", "sent", "failed"] as const;
export type CampaignStatusKey = (typeof campaignStatusKeys)[number];

/**
 * Billed SMS parts for one message: Persian goes out as UCS-2 (70 chars, 67 per part
 * when split), plain Latin text as GSM (160, 153 per part). 0 for an empty message.
 */
export const smsPartsOf = (message: string) => {
  const length = message.trim().length;
  if (length === 0) return 0;
  const [single, perPart] = /^[\x20-\x7e\n]*$/.test(message.trim()) ? [160, 153] : [70, 67];
  return length <= single ? 1 : Math.ceil(length / perPart);
};

/** Staff Roles a Lead or sales task may be assigned to */
export const leadOwnerRoles = ["sales_agent", "sales_manager"] as const;

/**
 * How a website Lead picks its agent: in turn among active agents, or in turn among the
 * agents of the vehicle's branch / brand (falling back to everyone when none match)
 */
export const leadRoutingModes = ["round_robin", "branch", "brand"] as const;
export type LeadRoutingMode = (typeof leadRoutingModes)[number];

export const leadRoutingSchema = z.object({
  mode: z.enum(leadRoutingModes).default("round_robin"),
  brandAgents: z.record(z.string().max(60), z.array(z.string().max(64)).max(50)).default({}),
  pausedIds: z.array(z.string().max(64)).max(200).default([]),
});
export type LeadRouting = z.infer<typeof leadRoutingSchema>;

/** one implementation with the auth phone-OTP hook */
export { normalizePhone };

// ---------------------------------------------------------------------------
// Customer Segments: a saved AND-filter over customer facts
// ---------------------------------------------------------------------------

export const segmentCriteriaSchema = z.object({
  /** loyalty tiers; empty = any */
  tiers: z.array(z.enum(tierKeys)).default([]),
  /** exact city; empty = any */
  city: z.string().trim().max(40).default(""),
  /** owns a vehicle whose name contains this text; empty = any */
  vehicleKeyword: z.string().trim().max(40).default(""),
  /** owns a vehicle bought in or before this Jalali year (warranty running out); null = any */
  vehicleYearTo: z.number().int().min(1380).max(1500).nullable().default(null),
  /** no recorded activity for at least this many days; null = any */
  inactiveDays: z.number().int().min(1).max(3650).nullable().default(null),
});
export type SegmentCriteria = z.infer<typeof segmentCriteriaSchema>;

export type CustomerFacts = {
  tier: TierKey;
  city: string;
  lastActivityAt: Date;
  vehicles: { name: string; purchaseYear: number }[];
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function matchesSegment(facts: CustomerFacts, criteria: SegmentCriteria, now: Date) {
  if (criteria.tiers.length > 0 && !criteria.tiers.includes(facts.tier)) return false;
  if (criteria.city !== "" && facts.city !== criteria.city) return false;
  if (
    criteria.vehicleKeyword !== "" &&
    !facts.vehicles.some((vehicle) => vehicle.name.includes(criteria.vehicleKeyword))
  ) {
    return false;
  }
  const yearTo = criteria.vehicleYearTo;
  if (yearTo !== null && !facts.vehicles.some((vehicle) => vehicle.purchaseYear <= yearTo)) {
    return false;
  }
  if (
    criteria.inactiveDays !== null &&
    now.getTime() - facts.lastActivityAt.getTime() < criteria.inactiveDays * DAY_MS
  ) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// "Today" for follow-ups and call counts is the dealership's day, not the server's
// ---------------------------------------------------------------------------

const TEHRAN_OFFSET_MS = 3.5 * 60 * 60 * 1000;

/** Instant at which the current calendar day began in Tehran (UTC+03:30, no DST since 1401) */
export const startOfTehranDay = (now: Date) => {
  const shifted = now.getTime() + TEHRAN_OFFSET_MS;
  return new Date(shifted - (shifted % DAY_MS) - TEHRAN_OFFSET_MS);
};

/** A date a person picked runs to the end of that day, as the printed proforma says */
export const endOfTehranDay = (at: Date) =>
  new Date(startOfTehranDay(at).getTime() + DAY_MS - 60_000);

// ---------------------------------------------------------------------------
// Customer file: what staff note outside a Lead (customer_interaction)
// ---------------------------------------------------------------------------

export const interactionKindKeys = ["note", "call", "sms", "whatsapp"] as const;
export type InteractionKindKey = (typeof interactionKindKeys)[number];
